import { useState } from "react";
import { Link } from "react-router-dom";
import { apiPost } from "../legacy/services/apiClient";

const INITIAL_FORM = {
  name: "",
  email: "",
  phone: "",
  destination_text: "",
  start_date: "",
  end_date: "",
  travelers: "2",
  budget_min: "",
  budget_max: "",
  message: "",
};

export default function CustomTourRequestPage() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setStatus({ type: "error", message: "Name and email are required." });
      return;
    }

    setSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      await apiPost("/api/public/custom-tour-requests", {
        ...form,
        travelers: form.travelers ? Number(form.travelers) : undefined,
        budget_min: form.budget_min ? Number(form.budget_min) : undefined,
        budget_max: form.budget_max ? Number(form.budget_max) : undefined,
      });
      setForm(INITIAL_FORM);
      setStatus({
        type: "success",
        message: "Request submitted. Our team will contact you shortly.",
      });
    } catch (err) {
      setStatus({
        type: "error",
        message: err?.message || "Unable to submit your request right now.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--page-bg)]">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(201,169,110,0.22),transparent_32%),radial-gradient(circle_at_bottom_left,rgba(15,43,51,0.14),transparent_30%)]" />
        <div className="landing-container relative py-14">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.34em] text-[var(--gold)]">
              Custom Journeys
            </p>
            <h1 className="mt-4 font-homevilas text-4xl text-[var(--ink)] md:text-5xl">
              Request a custom tour
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-[var(--text-muted)] md:text-base">
              Share your destination, dates, and budget. The Homevilas team will design a tailored
              journey for stays, transport, and curated experiences.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/packages/list"
                className="rounded-full border border-[var(--border)] bg-white px-5 py-2.5 text-sm font-semibold text-[var(--ink)] shadow-soft-sm"
              >
                Browse packages
              </Link>
              <Link
                to="/contact"
                className="rounded-full border border-[rgba(201,161,91,0.4)] px-5 py-2.5 text-sm font-semibold text-[var(--gold)]"
              >
                Contact team
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-container pb-16">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_320px]">
          <form
            onSubmit={handleSubmit}
            className="rounded-3xl border border-[var(--border)] bg-white/95 p-6 shadow-[0_20px_50px_rgba(15,23,42,0.08)] md:p-8"
          >
            <div className="grid gap-5 md:grid-cols-2">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--muted)]">
                  Full name
                </span>
                <input
                  className="mt-2 w-full rounded-2xl border border-[var(--border)] px-4 py-3 text-sm text-[var(--ink)] outline-none"
                  value={form.name}
                  onChange={(event) => updateField("name", event.target.value)}
                  required
                />
              </label>

              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--muted)]">
                  Email
                </span>
                <input
                  type="email"
                  className="mt-2 w-full rounded-2xl border border-[var(--border)] px-4 py-3 text-sm text-[var(--ink)] outline-none"
                  value={form.email}
                  onChange={(event) => updateField("email", event.target.value)}
                  required
                />
              </label>

              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--muted)]">
                  Phone
                </span>
                <input
                  className="mt-2 w-full rounded-2xl border border-[var(--border)] px-4 py-3 text-sm text-[var(--ink)] outline-none"
                  value={form.phone}
                  onChange={(event) => updateField("phone", event.target.value)}
                />
              </label>

              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--muted)]">
                  Destination
                </span>
                <input
                  className="mt-2 w-full rounded-2xl border border-[var(--border)] px-4 py-3 text-sm text-[var(--ink)] outline-none"
                  placeholder="Goa, Kerala, Rajasthan..."
                  value={form.destination_text}
                  onChange={(event) => updateField("destination_text", event.target.value)}
                />
              </label>

              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--muted)]">
                  Start date
                </span>
                <input
                  type="date"
                  className="mt-2 w-full rounded-2xl border border-[var(--border)] px-4 py-3 text-sm text-[var(--ink)] outline-none"
                  value={form.start_date}
                  onChange={(event) => updateField("start_date", event.target.value)}
                />
              </label>

              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--muted)]">
                  End date
                </span>
                <input
                  type="date"
                  className="mt-2 w-full rounded-2xl border border-[var(--border)] px-4 py-3 text-sm text-[var(--ink)] outline-none"
                  value={form.end_date}
                  onChange={(event) => updateField("end_date", event.target.value)}
                />
              </label>

              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--muted)]">
                  Travellers
                </span>
                <input
                  type="number"
                  min="1"
                  className="mt-2 w-full rounded-2xl border border-[var(--border)] px-4 py-3 text-sm text-[var(--ink)] outline-none"
                  value={form.travelers}
                  onChange={(event) => updateField("travelers", event.target.value)}
                />
              </label>

              <div className="grid gap-5 sm:grid-cols-2">
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--muted)]">
                    Min budget
                  </span>
                  <input
                    type="number"
                    min="0"
                    className="mt-2 w-full rounded-2xl border border-[var(--border)] px-4 py-3 text-sm text-[var(--ink)] outline-none"
                    value={form.budget_min}
                    onChange={(event) => updateField("budget_min", event.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--muted)]">
                    Max budget
                  </span>
                  <input
                    type="number"
                    min="0"
                    className="mt-2 w-full rounded-2xl border border-[var(--border)] px-4 py-3 text-sm text-[var(--ink)] outline-none"
                    value={form.budget_max}
                    onChange={(event) => updateField("budget_max", event.target.value)}
                  />
                </label>
              </div>

              <label className="block md:col-span-2">
                <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--muted)]">
                  Message
                </span>
                <textarea
                  rows="5"
                  className="mt-2 w-full rounded-2xl border border-[var(--border)] px-4 py-3 text-sm text-[var(--ink)] outline-none"
                  placeholder="Tell us the kind of journey you want..."
                  value={form.message}
                  onChange={(event) => updateField("message", event.target.value)}
                />
              </label>
            </div>

            {status.message ? (
              <div
                className={`mt-5 rounded-2xl px-4 py-3 text-sm ${
                  status.type === "error"
                    ? "bg-rose-50 text-rose-700"
                    : "bg-emerald-50 text-emerald-700"
                }`}
              >
                {status.message}
              </div>
            ) : null}

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="rounded-full bg-[var(--cta-dark)] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[var(--cta-dark-hover)] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {submitting ? "Submitting..." : "Send request"}
              </button>
            </div>
          </form>

          <aside className="space-y-5">
            <div className="rounded-3xl border border-[var(--border)] bg-white/95 p-6 shadow-soft-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--muted)]">
                What to share
              </p>
              <div className="mt-4 space-y-3 text-sm text-[var(--text-muted)]">
                <p>Destination ideas or preferred region</p>
                <p>Travel dates or flexible month</p>
                <p>Group size and stay style</p>
                <p>Budget range and special requests</p>
              </div>
            </div>
            <div className="rounded-3xl border border-[var(--border)] bg-white/95 p-6 shadow-soft-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--muted)]">
                Need immediate help?
              </p>
              <div className="mt-4 space-y-2 text-sm text-[var(--text-muted)]">
                <p>info@homevilas.com</p>
                <p>meena@homevilas.com</p>
                <p>+918287703118</p>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
